import React, { useEffect, useState, useCallback } from 'react';
import Typography from 'apollo-react/components/Typography';
import Paper from 'apollo-react/components/Paper';
import Box from 'apollo-react/components/Box';
import Table from 'apollo-react/components/Table';
import Divider from 'apollo-react/components/Divider';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Download from 'apollo-react-icons/Download';
import { useFileDownload } from '../../../hooks/useFileDownload';
import axiosInstance from '../../../axiosInstance';

const EctdVersionsHistory = ({ requestId, persona = 'customer', role = '' }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { downloadFile, downloading } = useFileDownload(persona);

  const fetchVersions = useCallback(async () => {
    if (!requestId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const endpoint = persona === 'customer'
        ? `/api/ectd-versions/request/${requestId}`
        : `/api/ectd-versions/admin/request/${requestId}`;
      
      const response = await axiosInstance.get(endpoint);
      
      if (response.data && response.data.success) {
        setVersions(response.data.data || []);
      } else {
        setVersions([]);
      }
    } catch (err) {
      console.error('Error fetching ECTD versions:', err);
      setError(err.response?.data?.message || 'Failed to load ECTD version history');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [requestId, persona]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleDownload = async (version) => {
    if (!version.file_id) return;

    await downloadFile(
      version.file_id,
      version.file?.original_name || version.file?.file_name || `ectd-v${version.version_number}.zip`
    );
  };

  if (loading) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 200 }}>
          <CircularProgress variant="indeterminate" statusText="Loading..." />
        </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Box sx={{ padding: 2, backgroundColor: '#ffebee', borderRadius: 1 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Version History
          </Typography>
          <Typography color="error">{error}</Typography>
        </Box>
      </Paper>
    );
  }

  const columns = [
    {
      header: 'Version Number',
      accessor: 'version_number',
    },
    {
      header: 'File Name',
      accessor: 'file_name',
      customCell: ({ row }) => row.file?.original_name || row.file?.file_name || 'N/A',
    },
    {
      header: 'Sequence Number',
      accessor: 'sequence_number',
    },
    {
      header: 'Upload Date',
      accessor: 'createdAt',
      customCell: ({ row }) => {
        if (!row.createdAt) return 'N/A';
        const date = new Date(row.createdAt);
        return date.toLocaleDateString('en-GB');
      },
    },
    {
      header: 'Actions',
      accessor: 'actions',
      customCell: ({ row }) => (
        <Button
        variant="primary"
          size="small"
          onClick={() => handleDownload(row)}
          disabled={downloading || !row.file_id}
        >
          Download Attachment
        </Button>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: '', marginBottom: 2 }}
        >
          eCTD Version History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All versions of eCTD files submitted for this request
        </Typography>
      </Box>
      <Divider sx={{ marginBottom: 3 }} />

      {versions.length === 0 ? (
        <Box
          sx={{
            padding: 4,
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No eCTD versions found for this request
          </Typography>
        </Box>
      ) : (
        <Table
          columns={columns}
          rows={versions}
          rowId="id"
          hidePagination={versions.length <= 10}
          rowsPerPageOptions={[10, 25, 50, 'All']}
          defaultRowsPerPage={10}
          tablePaginationProps={{
            labelDisplayedRows: ({ from, to, count }) =>
              `Versions ${from}-${to} of ${count}`,
            truncate: true,
          }}
        />
      )}
    </>
  );
};

export default EctdVersionsHistory;

